import { Box, Dropdown, Flex, Image, Typography } from "@qws-org/ui-kit";
import type { FC } from "react";
import React, { useState } from "react";

import type { GetSettingsQuery as GetSettingsQuerySdk } from "~/core/sdk";
import { ArrowDown, Globe } from "~/icons";
import { useDirectus } from "~/shared/utils/directus/useDirectus";
import {
  getLocaleHref,
  type LocaleAlternate,
} from "~/shared/utils/locale-navigation";

export type LocaleSelectProps = {
  languages: GetSettingsQuerySdk["languages"];
  availableLanguages: Array<string>;
  language: string;
  hreflangAlternates?: Array<LocaleAlternate>;
  variant?: "static" | "positioned";
  offset?: number;
  isNonModal?: boolean;
};

function isLanguageCode(
  code: string | null | undefined,
  availableLanguages: Array<string>,
): code is LanguageCode {
  if (code) {
    return availableLanguages.includes(code);
  }
  return false;
}

export const LocaleSelect: FC<LocaleSelectProps> = ({
  languages,
  language,
  availableLanguages,
  hreflangAlternates,
  variant = "positioned",
  offset,
  isNonModal = true,
}) => {
  const { getDirectusImage } = useDirectus();
  const [isActive, setIsActive] = useState(false);
  const visibleLanguages = languages.filter(
    (item) => !item?.hide_in_language_switcher,
  );
  const selectedLang = languages?.find((item) => {
    if (item?.code === language) {
      return item;
    }

    return undefined;
  });
  const changeLanguage = (index: number): void => {
    const code = visibleLanguages[index]?.code;

    if (code === language || !isLanguageCode(code, availableLanguages)) {
      return;
    }

    location.href = getLocaleHref({
      code,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      alternates: hreflangAlternates,
    });
  };

  return (
    <Dropdown.Trigger
      onOpenChange={(isOpen) => setIsActive(isOpen)}
      onAction={(key) => {
        const index = Number(key);

        if (
          Number.isInteger(index) &&
          index >= 0 &&
          index < visibleLanguages.length
        ) {
          changeLanguage(index);
        }
      }}
      strategy="click"
      isNonModal={isNonModal}
      placement="top left"
      offset={offset}
      variant={variant}
      triggerElement={
        <button
          className="cursor-pointer border-0 bg-transparent p-0 text-inherit"
          data-testid="secondary-layout-locale-switch"
          aria-label={`Change language, current ${selectedLang?.code ?? language}`}
          type="button"
        >
          <Box
            bg={"surface-secondary-default"}
            border={{ radius: "button" }}
            minWidth={110}
          >
            <Flex
              align="center"
              justify="center"
              p={{ py: 8, px: 12 }}
              color="text-primary-default"
              hover={{ color: "text-primary-default" }}
              gap={4}
            >
              <Globe />
              <Typography
                fontSize="button.mobile.small"
                lineHeight="button.mobile.small"
                textTransform="uppercase"
                fontWeight="mobile.bold"
                md={{
                  fontWeight: "desktop.bold",
                  fontSize: "button.desktop.small",
                  lineHeight: "button.desktop.small",
                }}
                p={{ pr: 8, pl: 4 }}
              >
                {selectedLang ? selectedLang?.code : "Pick"}
              </Typography>
              <ArrowDown
                className={`${isActive && "transform rotate-[-90deg]"}`}
              />
            </Flex>
          </Box>
        </button>
      }
      dropdownWrapper={(wrapperProps) => (
        <Flex
          flexDirection="column"
          bg={"surface-secondary-default"}
          border={{ radius: "button" }}
          minWidth={110}
        >
          {wrapperProps.children}
        </Flex>
      )}
    >
      {visibleLanguages.map(
        (item, index) =>
          item && (
            <Dropdown.Item key={index} textValue={item?.code}>
              <Flex
                align="center"
                justify="flex-start"
                gap={8}
                key={index}
                p={{ py: 12, pl: 16 }}
                className="cursor-pointer"
                textTransform="uppercase"
                onClick={() => {
                  changeLanguage(index);
                }}
              >
                {item?.icon?.id && (
                  <Image
                    value={getDirectusImage(item.icon.id ?? "", {
                      format: "webp",
                      width: 24,
                    })}
                  />
                )}
                <Typography
                  fontSize="button.mobile.small"
                  lineHeight="button.mobile.small"
                  textTransform="uppercase"
                  fontWeight="mobile.bold"
                  md={{
                    fontWeight: "desktop.bold",
                    fontSize: "button.desktop.small",
                    lineHeight: "button.desktop.small",
                  }}
                >
                  {item?.code}
                </Typography>
              </Flex>
            </Dropdown.Item>
          ),
      )}
    </Dropdown.Trigger>
  );
};
